/**
 * Shared English labels for the Hose Builder Kits flow. Spread into each
 * project's customization bundle under `texts.kits` so all builds share one
 * source of truth. Per-project overrides can shadow individual keys afterwards.
 *
 * NOTE: the `showManageKits` toggle is intentionally NOT defined here. It is a
 * boolean served from rule_set.i18n.variables; seeding it (even as a string)
 * in any default bundle would block the server override (i18next deepExtend
 * can't overwrite a non-string primitive leaf). Read it via readBool().
 */
export const kitTexts = {
  myAssemblies: 'Build a Kit',
  myAssembliesLegacy: 'My assemblies',
  // The Manage Kits header button opens the saved-assemblies list, so it says so
  // regardless of what the confirm page calls its own badge button.
  manageMyAssembliesBtn: 'My assemblies',
  manageKits: 'Manage Kits',
  manageTitle: 'Manage Kits',
  manageSubtitle: 'Assign assemblies to kits for your order. Once added, they appear under that kit only.',
  searchPlaceholder: 'Search assemblies, parts',
  noResults: 'No matches.',
  save: 'Save',
  saveAndCopy: 'Save and Copy',
  saveAndBuildAnother: 'Save and Build Another',
  addToKit: 'Add to kit',
  // Display fallback for an assembly with no user-entered name, plus the
  // inline "recommended" hint and the warning badge shown next to it.
  untitledName: 'Untitled assembly',
  recommended: 'recommended',
  nameRecommended: 'name recommended',
  addParts: 'Add parts',
  createKit: 'Create kit',
  finishBtn: 'Finish and Generate PDF',
  // Confirm-page "Finish": generates the whole-workspace PDF directly (no popup)
  // and moves to the final screen.
  finish: 'Finish',
  generating: 'Generating PDF...',
  // Opens the combined preview popup (read-only). The PDF can also be generated
  // from inside it, but "Finish" no longer requires opening it first.
  pdfPreview: 'Preview',
  startOver: 'Start over',
  edit: 'Edit',
  copy: 'Copy',
  delete: 'Delete',
  collapse: 'Collapse',
  expand: 'Expand',
  removeLine: 'Remove from kit',
  removePart: 'Remove part',
  partsHeader: 'Additional Parts',
  kitNamePlaceholder: 'Kit name',
  kitNotesPlaceholder: 'Kit notes',
  assemblyNamePlaceholder: 'Assembly name',
  assemblyNotesPlaceholder: 'Notes',
  // {{count}} = assemblies + parts in the kit
  counts: '{{count}} items',
  pills: {
    untitled: 'Untitled',
    custom: 'Custom',
  },
  warnings: {
    lineCap: 'This kit is full ({{limit}} max). Remove a line to add another.',
  },
  tabs: {
    assemblies: 'My assemblies ({{count}})',
    kits: 'My kits ({{count}})',
  },
  assemblies: {
    title: 'My assemblies ({{count}})',
    subtitle: "Review what you've saved. Next, group assemblies for your order.",
    close: 'Close',
    empty: 'No saved assemblies yet. Build one in the wizard and save it.',
    // Header for assemblies not grouped into any kit, shown when kits exist.
    single: 'Single assemblies',
  },
  kits: {
    empty: 'No kits yet. Use Add to kit on a saved assembly to create one.',
  },
  deleteKit: {
    title: 'Delete this kit?',
    question: 'Delete "{{name}}"?',
    note: 'This kit has {{count}} items. Assemblies stay saved. They are just no longer grouped in this kit.',
    confirmBtn: 'Delete kit',
  },
  addAssemblies: {
    title: 'Add assemblies to kit',
    openBtn: 'Add assemblies',
    addBtn: 'Add',
    empty: 'No standalone assemblies available. Save an assembly first.',
  },
  picker: {
    title: 'Add to kit',
    pickLabel: 'Pick a kit',
    newKitBtn: '+ New kit',
    qtyLabel: 'Quantity to add',
    addBtn: 'Add to kit',
    full: 'Full',
    lineCount: '{{count}} items',
    empty: 'No kits yet. Create one to group assemblies on a single order.',
  },
  // Assembly name/notes fields shown on the confirm (last) page above the
  // action buttons. Leaving the name empty saves the assembly as "Untitled".
  confirmFields: {
    nameLabel: 'Assembly name',
    namePlaceholder: 'e.g., Front loader return line',
    nameHelp: 'A clear name makes line items legible in kits and on PDFs. Untitled assemblies are allowed but will show a warning.',
    notesLabel: 'Notes',
    notesOptional: '(optional)',
    notesPlaceholder: 'e.g., Routing constraint, color code, install reminder...',
  },
  // "Edit assembly" popup (name + notes only). To change the hose/fittings the
  // user copies the assembly and rebuilds it.
  editAssembly: {
    title: 'Edit assembly',
    nameLabel: 'Assembly name',
    namePlaceholder: 'Give it a recognizable name',
    nameHelp: 'To change hose or fittings, use Save and Copy on the confirm screen.',
    notesLabel: 'Notes',
    notesOptional: '(optional)',
    notesPlaceholder: '',
    saveBtn: 'Save changes',
  },
  newKit: {
    title: 'New kit',
    subtitle: 'Name your kit. You can add assemblies and accessories after creating it.',
    editTitle: 'Edit kit',
    editSubtitle: 'Update the kit name or notes.',
    saveBtn: 'Save changes',
    nameLabel: 'Kit name',
    nameHelp: '1-60 characters. Must be unique.',
    namePlaceholder: 'e.g., Tractor Project, Phase 1',
    notesLabel: 'Notes',
    notesOptional: '(optional)',
    notesPlaceholder: 'e.g., Job site, customer ref, install date',
    createBtn: 'Create kit',
    errorRequired: 'Kit name is required.',
    errorDuplicate: 'A kit with this name already exists.',
  },
  catalog: {
    title: 'Add parts',
    subtitle: "Clamps, guards, tags, and other items that aren't hose assemblies.",
    searchPlaceholder: 'Search parts by number or name',
    // Category filter chips under the search box. Only rendered when the
    // companions schema returns a category facet list.
    categoryFilter: 'Filter by category',
    allCategories: 'All',
    // {{shown}} loaded so far of {{total}} matches, {{lines}} accessory lines
    // already in this kit/assembly.
    showing: 'Showing {{shown}} of {{total}} parts · {{lines}} lines in kit',
    addBtn: 'Add to kit',
    // Shown instead of "Add to kit" once the part is already in this kit/assembly.
    addMore: 'Add more',
    // Badge on a card for a part already added to this kit/assembly.
    inKit: 'In kit ×{{qty}}',
    adding: 'Adding...',
    added: 'Part added',
    loadMore: 'Load more',
    noResults: 'No parts match your search. Enter a custom part below.',
    loadError: 'Could not load parts. Please try again.',
    addError: 'Could not add part. Please try again.',
    customToggle: 'Add a custom part',
    customPartLabel: 'Part number (required)',
    customNotesLabel: 'Notes',
    customPartRequired: 'Part number is required',
    cancel: 'Cancel',
    done: 'Done',
  },
  final: {
    title: 'Your PDF is ready',
    message: 'Your combined order PDF has been generated.',
    persistNote: 'Your kits stay saved until you start over.',
    addToCart: 'Add to Cart',
    email: 'Email',
    viewPdf: 'View PDF',
    failed: 'PDF generation failed. Please try again.',
  },
  confirm: {
    deleteKit: 'Delete this kit? Its assemblies stay saved as standalone.',
    deleteAssembly: 'Delete this assembly?',
  },
  // Delete-assembly confirmation dialog (standalone list).
  deleteAssembly: {
    title: 'Delete this assembly?',
    question: 'Delete this {{name}}?',
    note: 'This assembly is not used in any kit.',
    confirmBtn: 'Delete',
  },
  // "Start over" confirmation. Clears every assembly but keeps the (now empty)
  // kits so the user can refill them for the next order.
  startOverConfirm: {
    title: 'Start over?',
    message: 'This clears every assembly in this session. Your kits are kept (emptied) for reuse.',
    note: 'There is no undo.',
    confirmBtn: 'Yes, start over',
  },
  // Combined PDF preview popup (Manage Kits "Finish and Generate PDF").
  preview: {
    title: 'Combined PDF preview',
    subtitle: '{{kits}} kits · {{configs}} configurations · {{assemblies}} total assemblies · {{loose}} loose items',
    email: 'Email',
    download: 'Download PDF',
    close: 'Close',
  },
  toast: {
    saved: 'Assembly saved',
    saveError: 'Could not save. Please try again.',
    copied: 'Assembly saved, opening a copy...',
    lineAdded: 'Added to kit',
    lineRemoved: 'Removed from kit',
    kitCreated: 'Kit created',
    kitUpdated: 'Kit updated',
    kitDeleted: 'Kit deleted',
    assemblyUpdated: 'Assembly updated',
    assemblyDeleted: 'Assembly deleted',
    partRemoved: 'Part removed',
    limitReached: 'A kit can hold up to {{limit}} assemblies.',
    nothingToGenerate: 'Add at least one assembly or kit before generating.',
    acceptTerms: 'Please accept the terms to generate the PDF.',
    pdfError: 'PDF generation failed. Please try again.',
    updateError: 'Something went wrong. Please try again.',
    cartOnlyWhenEmbedded: 'Cart is only available when embedded.',
    missingSessionId: 'Configuration error: missing sessionId.',
    storageUnavailable: 'Storage is blocked, so your kits will not be saved if you refresh.',
  },
} as const;

export default kitTexts;
